import { useCallback, useEffect, useMemo, useState } from "react";
import ClaimDialog from "./components/ClaimDialog";
import LegalView from "./components/LegalView";
import SuccessView from "./components/SuccessView";
import { resolveSiteId, SITE_CONFIG } from "./site-config";
import { mergeCatalog } from "./slot-catalog";
import type { ClaimDraft, SiteState, Slot } from "./types";
import EarthSite from "./sites/EarthSite";
import HomesSite from "./sites/HomesSite";
import LandSite from "./sites/LandSite";
import ShopSite from "./sites/ShopSite";

const site = resolveSiteId();

interface ClaimSelection {
  slot: Slot;
  draft?: ClaimDraft;
}

export default function App() {
  const [state, setState] = useState<SiteState | null>(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<ClaimSelection | null>(null);

  const loadState = useCallback(async () => {
    try {
      const response = await fetch(`/api/state?site=${site}`, { cache: "no-store" });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error ?? "The public ledger is unavailable.");
      setState(body);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The public ledger is unavailable.");
    }
  }, []);

  useEffect(() => {
    void loadState();
  }, [loadState]);

  useEffect(() => {
    const metadata = SITE_CONFIG[site];
    document.title = metadata.socialTitle;
    const values: Array<[string, string]> = [
      ['meta[name="description"]', metadata.socialDescription],
      ['meta[property="og:title"]', metadata.socialTitle],
      ['meta[property="og:description"]', metadata.socialDescription],
      ['meta[name="twitter:title"]', metadata.socialTitle],
      ['meta[name="twitter:description"]', metadata.socialDescription],
    ];
    for (const [selector, content] of values) {
      document.querySelector<HTMLMetaElement>(selector)?.setAttribute("content", content);
    }
  }, []);

  const slots = useMemo(() => (state ? mergeCatalog(site, state.slots) : []), [state]);
  const openClaim = useCallback((slot: Slot, draft?: ClaimDraft) => {
    setSelected({ slot, draft });
  }, []);

  if (window.location.pathname === "/success") return <SuccessView site={site} />;
  if (["/rules", "/terms", "/privacy"].includes(window.location.pathname)) {
    return <LegalView site={site} page={window.location.pathname.slice(1) as "rules" | "terms" | "privacy"} />;
  }
  if (error) return <main className="fatal-error"><b>LEDGER OFFLINE</b><h1>{error}</h1><button onClick={() => window.location.reload()}>Kick it once</button></main>;
  if (!state) return <main className={`site-loading loading-${site}`}><span>OUTBID</span><b>{SITE_CONFIG[site].domain}</b></main>;

  const props = { slots, state, onClaim: openClaim };

  return (
    <>
      {site === "earth" && <EarthSite {...props} />}
      {site === "shop" && <ShopSite {...props} />}
      {site === "homes" && <HomesSite {...props} />}
      {site === "land" && <LandSite {...props} />}
      {selected && (
        <ClaimDialog
          site={site}
          slot={selected.slot}
          state={state}
          initialUrl={selected.draft?.url}
          initialAmountCents={selected.draft?.amountCents}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
